import { useMutation } from "@tanstack/react-query";
import { Loader2, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

import { assessScopeActivation } from "@/api/scope-activation";
import { Button } from "@/components/ui/button";

import { ScopeActivationResult } from "./ScopeActivationResult";

/**
 * Painel de aderência ECC → Scope Item (demo da Fase 5). Vive como aba do node
 * "Fit-to-Standard" (04, id "activation") no workbench, mostrado quando a
 * avaliação automática não rodou. Chama o endpoint /scope-activation/assess
 * (separado do SSE do pipeline) e renderiza o resultado com o renderer do
 * patternC (tiles + table). Lê a carga do ex-kit do projeto: sem conexão SAP
 * desde a #1785.
 */
export function ScopeActivationPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();

  const assess = useMutation({
    mutationFn: () => assessScopeActivation(projectId),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-2">
        <Button
          size="sm"
          className="h-8 gap-1.5"
          disabled={assess.isPending}
          onClick={() => assess.mutate()}
        >
          {assess.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Zap className="w-3.5 h-3.5" />
          )}
          {t("Assess Fit-to-Standard")}
        </Button>
      </div>

      {assess.isError && (
        <div className="text-xs text-red-600">
          {t("Assessment failed: {{message}}", {
            message: (assess.error as Error)?.message ?? t("error"),
          })}
        </div>
      )}

      {assess.isPending && (
        <div className="text-xs text-muted-foreground">
          {t("Reading the ex-kit load and evaluating the mapping rules...")}
        </div>
      )}

      {assess.data && <ScopeActivationResult data={assess.data} />}
    </div>
  );
}
